import { useRef, useState, type ReactNode } from 'react';

import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';

import styles from './ConfirmDialog.module.scss';
import { Dialog, DialogClose, DialogContent } from './Dialog';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  /** Что именно произойдёт. Последствия называть прямо, а не намёками. */
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` для необратимого, `accent` — для обычного подтверждения. */
  tone?: 'danger' | 'accent';
  /**
   * Фраза, которую нужно набрать, чтобы кнопка подтверждения ожила.
   *
   * Для по-настоящему опасного: удаление тенанта уносит с собой все его
   * сайты и весь контент. Набор названия превращает механическое нажатие в
   * осознанное — от промаха мышью это защищает надёжнее любого текста.
   */
  confirmPhrase?: string;
  /**
   * Действие. Может вернуть промис — тогда кнопка покажет загрузку, а окно
   * закроется только после успеха. Обработку и показ ошибки берёт на себя
   * вызывающий код: окно останется открытым, чтобы было куда вернуться.
   */
  onConfirm: () => void | Promise<void>;
  /** Дополнительное содержимое между описанием и полем подтверждения. */
  children?: ReactNode;
}

/**
 * Готовое подтверждение действия.
 *
 * Удаление встретится на десятках экранов, и собирать его каждый раз заново
 * — верный способ получить десять разных подтверждений с разными словами и
 * разным расположением кнопок.
 */
export function ConfirmDialog({ open, onOpenChange, ...rest }: ConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Содержимое существует только пока окно открыто, поэтому набранное
          и состояние загрузки сбрасываются сами. Сбрасывать их эффектом по
          изменению `open` не нужно: иначе окно, открытое второй раз для
          другого объекта, оказалось бы уже подтверждённым. */}
      {open && <ConfirmBody onOpenChange={onOpenChange} {...rest} />}
    </Dialog>
  );
}

type ConfirmBodyProps = Omit<ConfirmDialogProps, 'open'>;

function ConfirmBody({
  onOpenChange,
  title,
  description,
  confirmLabel = 'Подтвердить',
  cancelLabel = 'Отмена',
  tone = 'danger',
  confirmPhrase,
  onConfirm,
  children,
}: ConfirmBodyProps) {
  const [busy, setBusy] = useState(false);
  const [typed, setTyped] = useState('');
  const confirmRef = useRef<HTMLButtonElement>(null);
  const phraseRef = useRef<HTMLInputElement>(null);

  const phraseRequired = confirmPhrase !== undefined && confirmPhrase.length > 0;
  const phraseMatches = !phraseRequired || typed.trim() === confirmPhrase;

  const handleConfirm = async () => {
    setBusy(true);

    try {
      await onConfirm();
      onOpenChange(false);
    } catch {
      // Окно остаётся открытым: человеку нужно место, куда вернуться, а
      // сообщение об ошибке показывает вызывающий код.
      setBusy(false);
    }
  };

  return (
    <DialogContent
      size="sm"
      title={title}
      {...(description !== undefined ? { description } : {})}
      // Пока действие выполняется, закрывать нечего: половина работы уже
      // ушла на сервер.
      dismissible={!busy}
      onOpenAutoFocus={(event) => {
        event.preventDefault();

        // Фокус — на действии, которое человек и запросил: он нажал
        // «Удалить», окно лишь переспрашивает, и Enter должен отвечать «да»
        // (решение Давида, 23 сентября 2026 — до этого фокус стоял на
        // «Отмене», и Enter отменял то, что только что выбрали).
        // Где есть поле фразы — фокус в него: там подтверждение и так
        // требует набрать название.
        (phraseRef.current ?? confirmRef.current)?.focus();
      }}
      footer={
        <>
          <DialogClose asChild>
            <Button variant="outline" tone="neutral" disabled={busy}>
              {cancelLabel}
            </Button>
          </DialogClose>
          <Button
            ref={confirmRef}
            tone={tone}
            loading={busy}
            disabled={!phraseMatches}
            onClick={() => {
              void handleConfirm();
            }}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}

      {phraseRequired && (
        <div className={styles['confirm__phrase']}>
          <Field
            label={
              <>
                Введите <code className={styles['confirm__code']}>{confirmPhrase}</code>, чтобы
                подтвердить
              </>
            }
          >
            <Input
              ref={phraseRef}
              mono
              value={typed}
              onChange={(event) => {
                setTyped(event.target.value);
              }}
              autoComplete="off"
              spellCheck={false}
            />
          </Field>
        </div>
      )}
    </DialogContent>
  );
}
